import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mainSiteUrl } from '@/mocks/categories';
import JsonLd from '@/components/feature/JsonLd';
import { buildArticleSchema, buildFaqSchema } from '@/lib/schema';

const tableRows = [1, 2, 3, 4];

export default function ArticleContent() {
  const { t, i18n } = useTranslation();

  const schema = [
    buildArticleSchema({
      path: '/usfans-spreadsheet-guide',
      headline: t('article.title'),
      description: t('article.quickText'),
      language: i18n.language,
    }),
    buildFaqSchema([
      { question: t('article.faq1q'), answer: t('article.faq1a') },
      { question: t('article.faq2q'), answer: t('article.faq2a') },
      { question: t('article.faq3q'), answer: t('article.faq3a') },
      { question: t('article.faq4q'), answer: t('article.faq4a') },
    ]),
  ];

  const steps = [1, 2, 3, 4, 5];
  const listItems = [1, 2, 3, 4, 5, 6, 7, 8];
  const faqs = [1, 2, 3, 4];

  return (
    <article className="mx-auto max-w-3xl px-4 py-12 md:px-6 md:py-16">
      <JsonLd data={schema} />
      <nav className="flex items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-700">
          {t('nav.home')}
        </Link>
        <span>/</span>
        <span className="text-foreground-700">{t('article.title')}</span>
      </nav>

      <h1 className="mt-4 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
        {t('article.title')}
      </h1>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
        <span className="flex items-center gap-1.5">
          <i className="ri-user-line"></i>
          {t('article.author')}
        </span>
        <span className="flex items-center gap-1.5">
          <i className="ri-time-line"></i>
          {t('article.updated')}
        </span>
      </div>

      <div className="mt-6 rounded-lg border-l-4 border-primary-500 bg-primary-50 p-5">
        <p className="text-xs font-bold uppercase tracking-wide text-primary-700">
          {t('article.quickLabel')}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-foreground-700">
          {t('article.quickText')}
        </p>
      </div>

      <div className="mt-8 space-y-4 text-sm leading-relaxed text-foreground-700 md:text-base">
        <p>{t('article.intro1')}</p>
        <p>{t('article.intro2')}</p>
      </div>

      <section className="mt-10">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          {t('article.s1Title')}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
          {t('article.s1p1')}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
          {t('article.s1p2')}
        </p>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          {t('article.s2Title')}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
          {t('article.s2p1')}
        </p>
        <ul className="mt-4 space-y-2">
          {listItems.map((n) => (
            <li key={n} className="flex items-start gap-2 text-sm text-foreground-700 md:text-base">
              <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
              <span>{t(`article.s2Item${n}`)}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          {t('article.s3Title')}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
          {t('article.s3p1')}
        </p>
        <ol className="mt-4 space-y-3">
          {steps.map((n) => (
            <li key={n} className="flex items-start gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-white">
                {n}
              </span>
              <span className="text-sm leading-relaxed text-foreground-700 md:text-base">
                {t(`article.s3Step${n}`)}
              </span>
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          {t('article.s4Title')}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
          {t('article.s4p1')}
        </p>
        <div className="mt-5 overflow-x-auto rounded-lg border border-background-200">
          <table className="w-full min-w-[520px] text-left text-sm">
            <thead>
              <tr className="bg-background-100 text-foreground-950">
                <th className="px-4 py-3 font-bold">{t('article.s4HeadFactor')}</th>
                <th className="px-4 py-3 font-bold">{t('article.s4HeadCheck')}</th>
                <th className="px-4 py-3 font-bold">{t('article.s4HeadWhy')}</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((n) => (
                <tr key={n} className="border-t border-background-200">
                  <td className="px-4 py-3 font-semibold text-foreground-900">
                    {t(`article.s4r${n}Factor`)}
                  </td>
                  <td className="px-4 py-3 text-foreground-700">{t(`article.s4r${n}Check`)}</td>
                  <td className="px-4 py-3 text-foreground-600">{t(`article.s4r${n}Why`)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          {t('article.s5Title')}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
          {t('article.s5p1')}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
          {t('article.s5p2')}
        </p>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          {t('article.s6Title')}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
          {t('article.s6p1')}
        </p>
      </section>

      <section className="mt-12">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          {t('article.faqTitle')}
        </h2>
        <div className="mt-4 space-y-3">
          {faqs.map((n) => (
            <div key={n} className="rounded-lg border border-background-200 bg-white p-5">
              <h3 className="text-sm font-bold text-foreground-900 md:text-base">
                {t(`article.faq${n}q`)}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                {t(`article.faq${n}a`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12 rounded-lg bg-primary-500 p-8 text-center">
        <h2 className="font-heading text-xl font-bold text-white md:text-2xl">
          {t('article.ctaTitle')}
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-sm text-primary-50 md:text-base">
          {t('article.ctaText')}
        </p>
        <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="w-full whitespace-nowrap rounded-md bg-white px-6 py-3 text-sm font-bold text-primary-700 transition-colors hover:bg-primary-50 sm:w-auto"
          >
            {t('article.ctaButton')}
          </a>
          <Link
            to="/"
            className="w-full whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-accent-600 sm:w-auto"
          >
            {t('article.backHome')}
          </Link>
        </div>
      </section>
    </article>
  );
}